import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from '@tanstack/react-router';
import type {
  Order, OrderChannel, OrderLine, Party, Product, TimeWindow,
} from '@prody/shared';
import {
  OrderChannelSchema, computeOrderTotals, hasRole, money as makeMoney,
} from '@prody/shared';
import {
  Button, Code, EmptyState, Field, IconClose, IconPlus, Input, ListPane,
  PageHeader, Section, Select, Textarea, Toolbar, Workbench, toast,
} from '~/design-system';
import { businessPath, path } from '~/app/navigation';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import {
  useCreateOrder, useOrder, useParties, useProducts, useQuotes, useUpdateOrder,
} from '~/platform/data/queries';
import { productName } from '~/platform/domain/presentation';
import './orders.css';

/* ==================================================================
   THE ORDER PAD.

   One screen for both "a chef just called" and "the chef changed
   their mind". Lines are the work surface: pick a product, the unit
   and today's price follow automatically; type over the price and it
   is honestly marked as typed by hand. Totals recompute on every
   keystroke through the same arithmetic the list and the invoice use.
   ================================================================== */

interface LineDraft {
  key: string;
  /** Present when this row already exists on the order — edits keep its history. */
  original: OrderLine | null;
  productId: string;
  quantity: string;
  price: string;
  priceSource: OrderLine['priceSource'];
  note: string;
}

let draftSeq = 0;
const blankLine = (): LineDraft => ({
  key: `draft_${++draftSeq}`,
  original: null,
  productId: '',
  quantity: '',
  price: '',
  priceSource: 'manual',
  note: '',
});

const parseNum = (raw: string): number => Number(raw.replace(',', '.'));

export default function OrderEditorPage() {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const { business, format } = useBusiness();
  const navigate = useNavigate();
  const { orderId } = useParams({ strict: false }) as { orderId?: string };
  const isEdit = Boolean(orderId);

  const base = businessPath(business.slug);
  const tradingDay = format.tradingDay();

  const { data: existing, isLoading: loadingOrder } = useOrder(business.id, orderId ?? null);
  const { data: parties = [] } = useParties(business.id);
  const { data: products = [] } = useProducts(business.id);
  const { data: quotes = [] } = useQuotes(business.id, tradingDay);
  const createOrder = useCreateOrder(business.id);
  const updateOrder = useUpdateOrder(business.id);

  const customers = useMemo(() => parties.filter((p) => hasRole(p, 'customer')), [parties]);
  const productsById = useMemo(
    () => new Map<string, Product>(products.map((p) => [p.id as string, p])),
    [products],
  );
  const sellQuoteFor = useMemo(
    () => new Map(quotes.map((q) => [q.productId as string, q.sell])),
    [quotes],
  );

  /* ---------------- Form state ---------------- */

  const [customerId, setCustomerId] = useState('');
  const [deliveryDate, setDeliveryDate] = useState(tradingDay);
  const [windowFrom, setWindowFrom] = useState('');
  const [windowTo, setWindowTo] = useState('');
  const [channel, setChannel] = useState<OrderChannel>('phone');
  const [customerNote, setCustomerNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([blankLine()]);
  const [touched, setTouched] = useState(false);

  /* Load the existing order into the pad exactly once. */
  const [loadedId, setLoadedId] = useState<string | null>(null);
  useEffect(() => {
    if (!isEdit || !existing || loadedId === existing.id) return;
    setLoadedId(existing.id);
    setCustomerId(existing.customerId as string);
    setDeliveryDate(existing.deliveryDate);
    setWindowFrom(existing.deliveryWindow?.from ?? '');
    setWindowTo(existing.deliveryWindow?.to ?? '');
    setChannel(existing.channel);
    setCustomerNote(existing.customerNote ?? '');
    setInternalNote(existing.internalNote ?? '');
    setLines(
      existing.lines.map((l) => ({
        key: l.id as string,
        original: l,
        productId: l.productId as string,
        quantity: String(l.ordered.value),
        price: (l.unitPrice.minor / 100).toFixed(2),
        priceSource: l.priceSource,
        note: l.note ?? '',
      })),
    );
  }, [isEdit, existing, loadedId]);

  /* When the customer changes, offer their delivery window as the default. */
  const customer = customers.find((p) => p.id === customerId) ?? null;
  const applyCustomerDefaults = (party: Party | null) => {
    const pref = party?.deliveryPreferences[0];
    if (pref && !windowFrom && !windowTo) {
      setWindowFrom(pref.window.from);
      setWindowTo(pref.window.to);
    }
  };

  const patchLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const onProductPicked = (row: LineDraft, productId: string) => {
    const product = productsById.get(productId);
    const quote = sellQuoteFor.get(productId);
    patchLine(row.key, {
      productId,
      // Today's board price arrives with the product; typing over it
      // flips the source to `manual` so the audit trail stays honest.
      price: quote ? (quote.minor / 100).toFixed(2) : row.price,
      priceSource: quote ? 'market' : 'manual',
      quantity: row.quantity || String(product?.minQuantity ?? 1),
    });
  };

  /* ---------------- Validation ---------------- */

  const windowInvalid =
    (windowFrom !== '') !== (windowTo !== '') ||
    (windowFrom !== '' && windowTo !== '' && windowFrom >= windowTo);

  const filledLines = lines.filter((l) => l.productId !== '');
  const lineInvalid = (l: LineDraft): boolean => {
    const qty = parseNum(l.quantity);
    const price = parseNum(l.price);
    return !Number.isFinite(qty) || qty <= 0 || !Number.isFinite(price) || price < 0;
  };
  const invalid =
    customerId === '' || filledLines.length === 0 || windowInvalid || filledLines.some(lineInvalid);

  const buildLine = (l: LineDraft, index: number): OrderLine => {
    const product = productsById.get(l.productId);
    const unit = product?.salesUnit ?? l.original?.ordered.unit ?? 'pc';
    const priceMinor = Math.round(parseNum(l.price) * 100);
    const keepHistory = l.original && l.original.productId === l.productId;
    const priceChanged =
      l.original && l.original.unitPrice.minor !== priceMinor ? 'manual' : l.priceSource;
    return {
      id: (keepHistory ? l.original!.id : `oln_edit_${Date.now()}_${index}`) as OrderLine['id'],
      productId: l.productId as OrderLine['productId'],
      productName: product?.name['en'] ?? l.original?.productName ?? l.productId,
      sku: product?.sku ?? l.original?.sku ?? l.productId,
      ordered: { value: parseNum(l.quantity), unit },
      picked: keepHistory ? l.original!.picked : null,
      invoiced: keepHistory ? l.original!.invoiced : null,
      unitPrice: makeMoney(priceMinor, business.settings.currency),
      priceSource: keepHistory ? priceChanged : l.priceSource,
      discountBps: keepHistory ? l.original!.discountBps : 0,
      taxRateKey:
        product?.taxRateKey ??
        l.original?.taxRateKey ??
        business.settings.taxScheme.defaultRateKey,
      supplierId: (keepHistory ? l.original!.supplierId : null) as OrderLine['supplierId'],
      supplierName: keepHistory ? l.original!.supplierName : null,
      shortfall: keepHistory ? l.original!.shortfall : null,
      substitution: keepHistory ? l.original!.substitution : null,
      note: l.note.trim() || null,
      sortOrder: index + 1,
    };
  };

  /* ---------------- Live totals ---------------- */

  const taxBpsFor = useMemo(() => {
    const rates = new Map(business.settings.taxScheme.rates.map((r) => [r.key, r.bps]));
    return (key: string) => rates.get(key) ?? 0;
  }, [business.settings.taxScheme]);

  const draft: Order = {
    ...(existing ?? ({} as Order)),
    currency: business.settings.currency,
    documentDiscountBps: existing?.documentDiscountBps ?? 0,
    deliveryFee: existing?.deliveryFee ?? null,
    lines: filledLines.filter((l) => !lineInvalid(l)).map((l, i) => buildLine(l, i)),
  } as Order;
  const draftTotals = computeOrderTotals(draft, taxBpsFor);

  /* ---------------- Save ---------------- */

  const window: TimeWindow | null =
    windowFrom && windowTo && !windowInvalid ? { from: windowFrom, to: windowTo } : null;

  const saveNew = (status: 'draft' | 'placed') => {
    setTouched(true);
    if (invalid) return;
    createOrder.mutate(
      {
        customerId,
        deliveryDate,
        deliveryWindow: window,
        channel,
        customerNote: customerNote.trim() || null,
        internalNote: internalNote.trim() || null,
        status,
        lines: filledLines.map((l) => ({
          productId: l.productId,
          quantity: {
            value: parseNum(l.quantity),
            unit: productsById.get(l.productId)?.salesUnit ?? 'pc',
          },
          unitPrice: makeMoney(Math.round(parseNum(l.price) * 100), business.settings.currency),
          priceSource: l.priceSource,
          note: l.note.trim() || null,
        })),
      },
      {
        onSuccess: (order) => {
          toast.ok(t('orderPlaced', { number: order.number }), {
            detail: t(`status.${order.status}`),
          });
          void navigate({ to: path(`${base}/orders/${order.id}`) });
        },
        onError: () => toast.error(t('common:errors.saveFailed')),
      },
    );
  };

  const saveEdit = () => {
    setTouched(true);
    if (invalid || !existing) return;
    const patch: Partial<Order> = {
      customerId: customerId as Order['customerId'],
      customerName: customer?.name ?? existing.customerName,
      deliveryDate,
      deliveryWindow: window,
      channel,
      customerNote: customerNote.trim() || null,
      internalNote: internalNote.trim() || null,
      lines: filledLines.map((l, i) => buildLine(l, i)),
    };
    updateOrder.mutate(
      { orderId: existing.id, patch },
      {
        onSuccess: () => {
          toast.ok(t('editor.saved', { number: existing.number }));
          void navigate({ to: path(`${base}/orders/${existing.id}`) });
        },
        onError: () => toast.error(t('common:errors.saveFailed')),
      },
    );
  };

  const busy = createOrder.isPending || updateOrder.isPending;

  /* ---------------- Guards ---------------- */

  if (isEdit && loadingOrder) {
    return (
      <Workbench>
        <div style={{ padding: 'var(--s-7)' }} aria-busy="true">
          <span className="hatch" style={{ display: 'block', blockSize: '1.4em', maxInlineSize: 260 }} />
        </div>
      </Workbench>
    );
  }

  if (isEdit && !existing) {
    return (
      <Workbench>
        <ListPane>
          <EmptyState
            title={t('common:errors.notFound')}
            body={t('common:errors.notFoundBody')}
            action={
              <Button variant="primary" onClick={() => void navigate({ to: path(`${base}/orders`) })}>
                {t('common:actions.back')}
              </Button>
            }
          />
        </ListPane>
      </Workbench>
    );
  }

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={
          isEdit ? (
            <span className="row-baseline">
              {t('editor.titleEdit')} <Code>{existing!.number}</Code>
            </span>
          ) : (
            t('newOrder')
          )
        }
        actions={
          <>
            <Button
              variant="quiet"
              onClick={() =>
                void navigate({
                  to: path(isEdit ? `${base}/orders/${orderId}` : `${base}/orders`),
                })
              }
            >
              {t('common:actions.cancel')}
            </Button>
            {isEdit ? (
              <Button variant="primary" busy={busy} disabled={touched && invalid} onClick={saveEdit}>
                {t('common:actions.saveChanges')}
              </Button>
            ) : (
              <>
                <Button variant="default" busy={busy} disabled={touched && invalid} onClick={() => saveNew('draft')}>
                  {t('editor.saveDraft')}
                </Button>
                <Button variant="primary" busy={busy} disabled={touched && invalid} onClick={() => saveNew('placed')}>
                  {t('placeOrder')}
                </Button>
              </>
            )}
          </>
        }
      />

      <Toolbar />

      <ListPane>
        <div className="pd-oedit">
          <div className="pd-oedit__grid">
            {/* ---------------- Lines ---------------- */}
            <div className="pd-oedit__lines">
              <Section
                title={t('detail.lines')}
                actions={
                  <Button
                    variant="quiet"
                    size="sm"
                    icon={<IconPlus />}
                    onClick={() => setLines((prev) => [...prev, blankLine()])}
                  >
                    {t('detail.addLine')}
                  </Button>
                }
              >
                {touched && filledLines.length === 0 ? (
                  <p className="pd-oedit__error" role="alert">
                    {t('editor.noLines')}
                  </p>
                ) : null}

                <div className="pd-oedit__rows">
                  {lines.map((row) => {
                    const product = productsById.get(row.productId);
                    const unit = product?.salesUnit ?? 'pc';
                    const rowInvalid = touched && row.productId !== '' && lineInvalid(row);
                    return (
                      <div
                        key={row.key}
                        className="pd-oedit__row"
                        data-hc={product?.handlingClass ?? 'neutral'}
                        data-invalid={rowInvalid || undefined}
                      >
                        <span className="pd-band" aria-hidden="true" />

                        <div className="pd-oedit__cell pd-oedit__product">
                          <label className="stencil" htmlFor={`prod-${row.key}`}>
                            {t('common:labels.product')}
                          </label>
                          <Select
                            id={`prod-${row.key}`}
                            value={row.productId}
                            onChange={(e) => onProductPicked(row, e.target.value)}
                          >
                            <option value="">{t('editor.selectProduct')}</option>
                            {products.map((p) => (
                              <option key={p.id} value={p.id}>
                                {productName(p, i18n.resolvedLanguage ?? 'en')} · {p.sku}
                              </option>
                            ))}
                          </Select>
                        </div>

                        <div className="pd-oedit__cell pd-oedit__qty">
                          <label className="stencil" htmlFor={`qty-${row.key}`}>
                            {t('common:labels.quantity')}
                          </label>
                          <Input
                            id={`qty-${row.key}`}
                            numeric
                            value={row.quantity}
                            suffix={unit}
                            onChange={(e) => patchLine(row.key, { quantity: e.target.value })}
                          />
                        </div>

                        <div className="pd-oedit__cell pd-oedit__price">
                          <label className="stencil" htmlFor={`price-${row.key}`}>
                            {t('common:labels.price')}
                            {row.priceSource === 'market' ? (
                              <span className="pd-oedit__source" title={t('detail.priceSource.market', { date: tradingDay })}>
                                ◆
                              </span>
                            ) : null}
                          </label>
                          <Input
                            id={`price-${row.key}`}
                            numeric
                            value={row.price}
                            suffix={format.currencySymbol()}
                            onChange={(e) =>
                              patchLine(row.key, { price: e.target.value, priceSource: 'manual' })
                            }
                          />
                        </div>

                        <div className="pd-oedit__cell pd-oedit__note">
                          <label className="stencil" htmlFor={`note-${row.key}`}>
                            {t('common:labels.notes')}
                          </label>
                          <Input
                            id={`note-${row.key}`}
                            value={row.note}
                            placeholder={t('editor.notePlaceholder')}
                            onChange={(e) => patchLine(row.key, { note: e.target.value })}
                          />
                        </div>

                        <div className="pd-oedit__cell pd-oedit__line-total">
                          <span className="stencil">{t('common:labels.total')}</span>
                          <span className="num">
                            {Number.isFinite(parseNum(row.quantity)) && Number.isFinite(parseNum(row.price))
                              ? format.money(
                                  makeMoney(
                                    Math.round(parseNum(row.quantity) * parseNum(row.price) * 100),
                                    business.settings.currency,
                                  ),
                                  { bare: true },
                                )
                              : '—'}
                          </span>
                        </div>

                        <Button
                          className="pd-oedit__remove"
                          variant="quiet"
                          size="sm"
                          iconOnly
                          aria-label={t('editor.removeLine')}
                          icon={<IconClose />}
                          onClick={() =>
                            setLines((prev) =>
                              prev.length > 1 ? prev.filter((l) => l.key !== row.key) : [blankLine()],
                            )
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </Section>

              <Section>
                <dl className="pd-totals">
                  <div>
                    <dt className="stencil">{t('common:labels.net')}</dt>
                    <dd className="num">{format.money(draftTotals.net)}</dd>
                  </div>
                  <div>
                    <dt className="stencil">{t('common:labels.tax')}</dt>
                    <dd className="num">{format.money(draftTotals.tax)}</dd>
                  </div>
                  <div className="pd-totals__grand">
                    <dt className="stencil">{t('common:labels.total')}</dt>
                    <dd className="num" style={{ fontSize: 'var(--t-title)' }}>
                      {format.money(draftTotals.gross)}
                    </dd>
                  </div>
                </dl>
              </Section>
            </div>

            {/* ---------------- Order facts ---------------- */}
            <aside className="pd-oedit__side">
              <Section title={t('common:labels.customer')}>
                <Field
                  label={t('common:labels.customer')}
                  required
                  error={touched && customerId === '' ? t('common:errors.required') : null}
                >
                  {({ id, invalid: isInvalid }) => (
                    <Select
                      id={id}
                      invalid={isInvalid}
                      value={customerId}
                      onChange={(e) => {
                        setCustomerId(e.target.value);
                        applyCustomerDefaults(customers.find((p) => p.id === e.target.value) ?? null);
                      }}
                    >
                      <option value="">{t('editor.selectCustomer')}</option>
                      {customers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} · {p.code}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field label={t('channel')}>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={channel}
                      onChange={(e) => setChannel(e.target.value as OrderChannel)}
                    >
                      {OrderChannelSchema.options.map((c) => (
                        <option key={c} value={c}>
                          {t(`channels.${c}`)}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </Section>

              <Section title={t('deliveryDate')}>
                <Field label={t('common:labels.date')} required>
                  {({ id }) => (
                    <Input
                      id={id}
                      type="date"
                      value={deliveryDate}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                    />
                  )}
                </Field>

                <Field
                  label={t('common:labels.window')}
                  hint={t('editor.windowHint')}
                  error={touched && windowInvalid ? t('editor.invalidWindow') : null}
                >
                  {({ invalid: isInvalid }) => (
                    <div className="row">
                      <Input
                        type="time"
                        aria-label={t('editor.windowFrom')}
                        invalid={isInvalid}
                        value={windowFrom}
                        onChange={(e) => setWindowFrom(e.target.value)}
                      />
                      <span className="dim">–</span>
                      <Input
                        type="time"
                        aria-label={t('editor.windowTo')}
                        invalid={isInvalid}
                        value={windowTo}
                        onChange={(e) => setWindowTo(e.target.value)}
                      />
                    </div>
                  )}
                </Field>
              </Section>

              <Section title={t('common:labels.notes')}>
                <Field label={t('common:labels.customerNote')}>
                  {({ id }) => (
                    <Textarea
                      id={id}
                      rows={2}
                      value={customerNote}
                      onChange={(e) => setCustomerNote(e.target.value)}
                    />
                  )}
                </Field>
                <Field label={t('common:labels.internalNote')} description={t('detail.reasonHint')}>
                  {({ id }) => (
                    <Textarea
                      id={id}
                      rows={2}
                      value={internalNote}
                      onChange={(e) => setInternalNote(e.target.value)}
                    />
                  )}
                </Field>
              </Section>
            </aside>
          </div>
        </div>
      </ListPane>
    </Workbench>
  );
}
